import React, { useState, useEffect } from 'react';
import {
  BookOpen, Search, Shield, Lightbulb, Briefcase, CheckSquare
} from 'lucide-react';
import api from '../services/api';
import DocxViewer from './DocxViewer';

const KnowledgeBase = () => {
  const [docxFiles, setDocxFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [contentLoading, setContentLoading] = useState(false);

  // Icon mapping for DOCX files
  const iconMap = {
    Shield,
    Lightbulb,
    BookOpen,
    Briefcase,
    CheckSquare
  };

  // Category translations
  const categoryNames = {
    sales: 'Продажи',
    training: 'Обучение',
    services: 'Услуги',
    process: 'Процессы',
    general: 'Общее'
  };

  // Fetch DOCX files on mount
  useEffect(() => {
    fetchDocxFiles();
  }, []);

  // Filter files when category or search changes
  useEffect(() => {
    filterFiles();
  }, [selectedCategory, searchQuery, docxFiles]);

  const fetchDocxFiles = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/docx/files');

      if (response.data.success) {
        setDocxFiles(response.data.files);
      }
    } catch (error) {
      console.error('Error fetching DOCX files:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterFiles = () => {
    let filtered = docxFiles;

    // Filter by category
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(f => f.category === selectedCategory);
    }

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(f =>
        f.display_name.toLowerCase().includes(query) ||
        (f.description && f.description.toLowerCase().includes(query))
      );
    }

    setFilteredFiles(filtered);
  };

  const handleFileSelect = async (file) => {
    setSelectedFile(file);
    setContentLoading(true);
    setFileContent(null);

    try {
      const response = await api.get(`/api/docx/content/${encodeURIComponent(file.filename)}`);

      if (response.data.success) {
        setFileContent(response.data.html);
      }
    } catch (error) {
      console.error('Error loading DOCX content:', error);
    } finally {
      setContentLoading(false);
    }
  };

  const handleBackToList = () => {
    setSelectedFile(null);
    setFileContent(null);
  };

  const getIcon = (iconName) => {
    const Icon = iconMap[iconName] || BookOpen;
    return <Icon className="w-5 h-5" />;
  };

  // Get unique categories with counts
  const categories = [
    { id: 'all', name: 'Все', count: docxFiles.length },
    ...Object.keys(categoryNames).map(catId => ({
      id: catId,
      name: categoryNames[catId],
      count: docxFiles.filter(f => f.category === catId).length
    })).filter(cat => cat.count > 0)
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <BookOpen className="w-12 h-12 text-blue-500 animate-pulse mx-auto mb-2" />
          <p className="text-slate-600">Загрузка материалов...</p>
        </div>
      </div>
    );
  }

  // If a file is selected, show the viewer
  if (selectedFile) {
    return (
      <div className="container mx-auto px-4 py-6">
        <DocxViewer
          document={selectedFile}
          htmlContent={fileContent}
          loading={contentLoading}
          onBack={handleBackToList}
        />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-slate-900 mb-2 flex items-center gap-3">
          <BookOpen className="w-8 h-8 text-blue-600" />
          База знаний
        </h1>
        <p className="text-slate-600">
          Документы, руководства и справочные материалы
        </p>
      </div>

      {/* Search and Filters */}
      <div className="mb-6 space-y-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Поиск по документам..."
            className="w-full pl-10 pr-4 py-3 border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
        </div>

        {/* Category Filter */}
        <div className="flex flex-wrap gap-2">
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                selectedCategory === category.id
                  ? 'bg-blue-600 text-white'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {category.name} ({category.count})
            </button>
          ))}
        </div>
      </div>

      {/* Documents Grid */}
      {filteredFiles.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredFiles.map((file) => (
            <div
              key={file.id}
              onClick={() => handleFileSelect(file)}
              className="p-5 border border-slate-200 rounded-lg hover:shadow-lg transition-shadow cursor-pointer bg-white"
            >
              <div className="flex items-start gap-3 mb-3">
                <div className={`p-2 rounded-lg bg-${file.color}-100 text-${file.color}-600`}>
                  {getIcon(file.icon)}
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-slate-900 mb-1">{file.display_name}</h3>
                  {file.description && (
                    <p className="text-sm text-slate-600 line-clamp-2">{file.description}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                <span className={`text-xs px-2 py-1 rounded bg-${file.color}-50 text-${file.color}-700 font-medium`}>
                  {categoryNames[file.category] || file.category}
                </span>
                {file.fileSizeMB && (
                  <span className="text-xs text-slate-500">
                    {file.fileSizeMB} МБ
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        /* No Results */
        <div className="text-center py-12">
          <BookOpen className="w-16 h-16 text-slate-300 mx-auto mb-4" />
          <p className="text-slate-600 text-lg">Документы не найдены</p>
          <p className="text-slate-500 text-sm">Попробуйте изменить фильтры или поисковый запрос</p>
        </div>
      )}
    </div>
  );
};

export default KnowledgeBase;
